import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Button,
  HStack,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from "@chakra-ui/react";
import {
  Post,
  DisplayMode,
  PostPersistantData,
  TimingState,
  shouldHidePost,
} from "./Post/Post";
import { getUnixTimestamp } from "../../Helpers/time";
import { PostEditingModal } from "./Post/PostInput";
import { HeadingFractionState } from "./Post/Fractions/Heading/HeadingFraction";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRangePicker } from "react-date-range";
import { adminPostApi, postApi } from "../../Api/BackendClient";
import { toast } from "react-toastify";
import { getBackupPosts } from "./BackupPosts";
import { Role } from "../../Session/SessionService";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export type AliasType = {
  [key: string]: string;
};

function PostFlow(props: {
  showLegend: boolean;
  category: string;
  selectedUserMode: Role | null;
  alias?: AliasType;
  openPostCreationModal: boolean;
  setOpenPostCreationModal: (open: boolean) => void;
  onLoadedPosts: (postCount: number) => void;
}) {
  const [posts, setPosts] = useState<PostPersistantData[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const handleFailureToLoadPosts = () => {
    toast.warning(
      "Kunde inte kontakta servern. Innehållet på denna sida är därför begränsat och möjligen utdaterat. Försök använda ett annat nätverk för att ladda sidan korrekt.",
      {
        toastId: 0,
        style: { color: "red" },
        position: "top-center",
        closeButton: true,
        autoClose: false,
      }
    );
    setPosts(getBackupPosts(props.category));
  };

  useEffect(() => {
    const fetchPosts = async () => {
      setLoadingPosts(true);
      if (
        props.selectedUserMode == Role.ADMIN ||
        props.selectedUserMode == Role.DEVELOPER
      ) {
        adminPostApi
          .getAllPosts(props.category)
          .then((response) => {
            setPosts(response.payload);
            if (!response.success) {
              handleFailureToLoadPosts();
              props.onLoadedPosts(0);
            } else {
              props.onLoadedPosts(response.payload.length);
            }
          })
          .finally(() => {
            setLoadingPosts(false);
          });
      } else {
        postApi
          .getPosts(props.category)
          .then((response) => {
            setPosts(response.payload);
            if (!response.success) {
              handleFailureToLoadPosts();
              props.onLoadedPosts(0);
            } else {
              props.onLoadedPosts(response.payload.length);
            }
          })
          .finally(() => {
            setLoadingPosts(false);
          });
      }
    };

    fetchPosts();
  }, [props.selectedUserMode]);

  const [postToEdit, setPostToEdit] = useState<undefined | PostPersistantData>(
    undefined
  );

  const [postForTimingModification, setPostForTimingModification] = useState<
    undefined | PostPersistantData
  >(undefined);

  const handleScrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (loadingPosts) {
    return (
      <div>
        <Spinner /> Laddar...
      </div>
    );
  }

  return (
    <div style={{ marginRight: "20px", marginLeft: "20px" }}>
      {/*Create blue box for table of contents*/}
      {props.showLegend && (
        <div>
          <Box
            border="1px"
            background={"blue.50"}
            borderColor="gray.300"
            p={4}
            wordBreak="break-word"
            display="inline-block"
          >
            <Heading size="sm" marginBottom={4}>
              Översikt
            </Heading>
            {posts
              .filter((p) => !shouldHidePost(p))
              .sort(postSortingRules)
              .map((post, index) => (
                <Text
                  key={post.id}
                  onClick={() => handleScrollTo(`post-${index}`)}
                  color="blue"
                  as="button"
                  display="block"
                  textAlign="left"
                  paddingStart="20px"
                  paddingTop={"10px"}
                  paddingBottom={"10px"}
                  paddingEnd="50px"
                >
                  {
                    index +
                      1 +
                      ". " +
                      (
                        post.fractions.find((p) => p.position === -1)
                          ?.state as HeadingFractionState
                      )?.text /** Not really what we want :P */
                  }
                </Text>
              ))}
          </Box>
        </div>
      )}
      {props.openPostCreationModal && (
        <PostEditingModal
          alias={props.alias}
          initialState={postToEdit}
          onClose={() => {
            props.setOpenPostCreationModal(false);
            setPostToEdit(undefined);
          }}
          onSave={(state: PostPersistantData) => {
            if (state.id == null) {
              adminPostApi
                .postPost(props.category, state)
                .then((reply) => {
                  state.id = reply.id;
                  setPosts((old) => [{ ...state }, ...old]);
                })
                .catch((e) => {
                  console.log(e);
                })
                .finally(() => {
                  props.setOpenPostCreationModal(false);
                  setPostToEdit(undefined);
                });
            } else {
              adminPostApi
                .putPost(props.category, state.id, state)
                .then(() => {
                  setPosts((old) => {
                    return old.map((p) => {
                      if (p.id === state.id) {
                        return state;
                      }
                      return p;
                    });
                  });
                })
                .catch((e) => {
                  console.log(e);
                })
                .finally(() => {
                  props.setOpenPostCreationModal(false);
                  setPostToEdit(undefined);
                });
            }
          }}
        />
      )}
      {postForTimingModification && (
        <PostTimingModal
          post={postForTimingModification}
          onSave={(state: PostPersistantData) => {
            setPosts((old) => {
              return old.map((p) => {
                if (postForTimingModification.id === p.id) {
                  return { ...state };
                }
                return { ...p };
              });
            });
            setPostForTimingModification(undefined);
            adminPostApi.putPost(
              props.category,
              postForTimingModification.id ?? "",
              state
            );
          }}
          onClose={() => {
            setPostForTimingModification(undefined);
          }}
        />
      )}
      {/* TODO: Temp fix (i hope), using key={JSON.stringify(posts)} to force a re-render after edit of a post is saved. For unknown reason it did not happen without it*/}
      {/* TODO: !!!!!!!!! TERRIBLE IDEA HERE!! better if works: <Box key={`posts-${props.category}`}></Box>*/}
      <Box key={JSON.stringify(posts)}>
        {posts
          .filter(
            (p) =>
              props.selectedUserMode === Role.ADMIN ||
              props.selectedUserMode === Role.DEVELOPER ||
              !shouldHidePost(p)
          )
          .sort(postSortingRules)
          .map((post, index) => (
            <div key={post.id} id={`post-${index}`}>
              <Post
                alias={props.alias ?? {}}
                onStateUpdate={(state: PostPersistantData) => {}}
                mode={
                  props.selectedUserMode === Role.ADMIN ||
                  props.selectedUserMode === Role.DEVELOPER
                    ? DisplayMode.DISPLAY_EDITABLE
                    : DisplayMode.DISPLAY
                }
                state={post}
                onPin={function (pin: boolean): void {
                  setPosts((old) =>
                    old.map((p) => {
                      if (p.id === post.id) {
                        return {
                          ...p,
                          pinTime: pin ? getUnixTimestamp() : undefined,
                        };
                      }
                      return p;
                    })
                  );
                  adminPostApi.putPost(props.category, post.id ?? "", {
                    ...post,
                    pinTime: pin ? getUnixTimestamp() : undefined,
                  });
                }}
                onHide={function (hidden: boolean): void {
                  setPosts((old) =>
                    old.map((p) => {
                      if (p.id === post.id) {
                        return { ...p, hidden: hidden };
                      }
                      return p;
                    })
                  );
                  adminPostApi.putPost(props.category, post.id ?? "", {
                    ...post,
                    hidden: hidden,
                  });
                }}
                onEdit={function (): void {
                  setPostToEdit({
                    ...post,
                    lastEditedTime: getUnixTimestamp(),
                  });
                  props.setOpenPostCreationModal(true);
                }}
                onCopy={function (): void {
                  console.log("COPY");
                  setPostToEdit({
                    ...post,
                    id: undefined,
                    hidden: false,
                    createTime: getUnixTimestamp(),
                    lastEditedTime: undefined,
                    pinTime: undefined,
                  });
                  props.setOpenPostCreationModal(true);
                }}
                onDelete={function (): void {
                  setPosts((old) => old.filter((p) => p.id !== post.id));
                  adminPostApi.deletePost(props.category, post.id ?? "");
                }}
                fractionInFocus={undefined}
                setFractionInFocus={function (id: string | undefined) {
                  /* Left blank */
                }}
                onSetTiming={function (timing: TimingState | undefined): void {
                  adminPostApi.putPost(props.category, post.id ?? "", {
                    ...post,
                    timing: timing,
                  });
                  setPosts((old) => {
                    return old.map((p) => {
                      if (p.id === post.id) {
                        return { ...p, timing: timing };
                      }
                      return p;
                    });
                  });
                }}
                onCreateNewTiming={function (): void {
                  setPostForTimingModification(post);
                }}
              />
            </div>
          ))}
      </Box>
    </div>
  );
}

export default PostFlow;

function postSortingRules(p1: PostPersistantData, p2: PostPersistantData) {
  // I think hidden ones should be shown in the place they appear if unhidden
  //const p1Hidden = shouldHidePost(p1);
  //const p2Hidden = shouldHidePost(p2);
  //if (p1Hidden !== p2Hidden) {
  //return (p2Hidden ? 0 : 1) - (p1Hidden ? 0 : 1);
  //}
  if (p1.pinTime || p2.pinTime) {
    return (p2.pinTime ?? 0) - (p1.pinTime ?? 0);
  }

  return (
    (p2.lastEditedTime ?? p2.createTime) - (p1.lastEditedTime ?? p1.createTime)
  );
}

const PostTimingModal = (props: {
  post: PostPersistantData;
  onSave: (state: PostPersistantData) => void;
  onClose: () => void;
}) => {
  const [selectionRange, setSelectionRange] = useState<{
    startDate: undefined | Date;
    endDate: undefined | Date;
    key: string;
  }>({
    startDate: props.post.timing?.showStartEpoch || new Date(),
    endDate: props.post.timing?.showEndEpoch,
    key: "selection",
  });

  return (
    <Modal
      closeOnOverlayClick={false}
      scrollBehavior={"inside"}
      isOpen={true}
      size={"5xl"}
      onClose={() => {
        props.onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent background={"white"}>
        <ModalHeader>Sätt visningstid för inlägg</ModalHeader>
        <ModalCloseButton />
        <ModalBody scrollBehavior={"auto"} margin={"20px"}>
          <div style={{ marginBottom: "60px" }}>
            <DateRangePicker
              ranges={[selectionRange]}
              onChange={(item) => {
                console.log(item.selection.startDate);
                setSelectionRange((old) => ({
                  key: old.key,
                  startDate: item.selection.startDate,
                  endDate:
                    item.selection.endDate &&
                    new Date(item.selection.endDate.getTime() + MS_PER_DAY - 1),
                }));
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter borderRadius={"10px"} background={"red"}>
          <HStack>
            <Spacer />
            <Button
              onClick={() => {
                props.onSave({
                  ...props.post,
                  timing: {
                    showStartEpoch: selectionRange.startDate,
                    showEndEpoch: selectionRange.endDate,
                  },
                });
              }}
            >
              {" "}
              Spara{" "}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
